import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  ViewChild
} from '@angular/core'
import { PlatformService } from '../../singleton-services/platform.service'
import { Subject, combineLatest } from 'rxjs'
import { HorizontalDirection } from './carousel.interface'
import { filter, map, takeUntil } from 'rxjs/operators'

@Component({
  selector: 'flo-carousel',
  styleUrls: ['./carousel.component.scss'],
  templateUrl: './carousel.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CarouselComponent implements OnChanges, AfterViewInit, OnDestroy {
  private destroy = new Subject<void>()

  private scrollActionSubject = new Subject<HorizontalDirection>()

  private scrollToItemSubject = new Subject<number>()

  private scrollElementSubject = new Subject<HTMLDivElement>()

  /** The container element holding the elements to scroll through. */
  private scrollElement$ = this.scrollElementSubject.asObservable()

  /** The elements contained within the carousel. */
  private elements$ = this.scrollElement$.pipe(
    filter(el => el.children.length > 0),
    filter(el => el.children[0].children !== null && el.children[0].children !== undefined),
    map(el => el.children[0].children)
  )

  /** Show the scroll based on these conditions. */
  public showScroll$ = this.elements$.pipe(map(xs => xs.length > 2))

  // tslint:disable-next-line:no-unused-variable
  private scrollAction$ = combineLatest([this.scrollActionSubject, this.scrollElement$, this.elements$])
    .pipe(takeUntil(this.destroy))
    .subscribe(([action, container, elements]) => {
      const elementCount = elements.length
      if (elementCount < 1) return

      const currentPosition = container.scrollLeft
      const containerWidth = container.scrollWidth
      const scrollWidth = containerWidth / elementCount
      if (action === 'left') {
        this.setScrollPosition(currentPosition - scrollWidth, container)
      } else {
        this.setScrollPosition(currentPosition + scrollWidth, container)
      }
    })

  // tslint:disable-next-line:no-unused-variable
  private scrollToItem$ = combineLatest([this.scrollToItemSubject, this.scrollElement$, this.elements$])
    .pipe(takeUntil(this.destroy))
    .subscribe(([index, container, elements]) => {
      const elementCount = elements.length
      const containerWidth = container.scrollWidth
      const scrollWidth = containerWidth / elementCount
      this.setScrollPosition(scrollWidth * index, container)
    })

  @ViewChild('scrollContainer')
  public set scrollContainer(el: ElementRef<HTMLDivElement>) {
    if (!!el.nativeElement) {
      this.scrollElementSubject.next(el.nativeElement)
    }
  }

  @Input() public headingText = 'Live Now'
  @Input() public itemIndex: number
  @Input() public border: '' | 'bottom'

  constructor(private ps: PlatformService) {}

  public scroll(direction: HorizontalDirection): void {
    this.scrollActionSubject.next(direction)
  }

  private setScrollPosition(position: number, el: HTMLElement): void {
    if (this.ps.isBrowser) {
      el.scrollTo({
        left: position,
        behavior: 'smooth'
      })
    }
  }

  ngOnChanges() {
    if (!this.itemIndex) return
    this.scrollToItemSubject.next(this.itemIndex)
  }

  ngAfterViewInit() {
    if (!this.itemIndex) return
    this.scrollToItemSubject.next(this.itemIndex)
  }

  ngOnDestroy() {
    this.destroy.next()
    this.destroy.complete()
  }
}
